import { useEffect, useMemo, useState } from "react";
import { api } from "../../api.js";
import { Badge, Button, Eyebrow, LoadingRows, Notice } from "../../components/ui.jsx";
import { ActionLink, PublicHeader } from "./PublicChrome.jsx";

export default function InvitePage({ token, authState, actor, account, onAccepted, onLogout }) {
  const [state, setState] = useState({ loading: true, invitation: null, error: "", accepted: false });
  const [busy, setBusy] = useState(false);
  const currentEmail = actor?.email || account?.email || null;

  useEffect(() => {
    document.title = "Organization invitation · RevPal";
    api.invitation(token)
      .then((data) => setState({ loading: false, invitation: data.invitation, error: "", accepted: false }))
      .catch((error) => setState({ loading: false, invitation: null, error: error.message, accepted: false }));
  }, [token]);

  const authLinks = useMemo(() => {
    const params = new URLSearchParams({ invite: token });
    if (state.invitation?.email) params.set("email", state.invitation.email);
    return {
      login: `/login?${params}`,
      signup: `/signup?${params}`,
    };
  }, [state.invitation?.email, token]);

  async function accept() {
    setBusy(true);
    setState((current) => ({ ...current, error: "" }));
    try {
      const data = await api.acceptInvitation(token);
      if (authState === "onboarding") onAccepted(data.invitation);
      else setState((current) => ({ ...current, invitation: data.invitation, accepted: true }));
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusy(false);
    }
  }

  const invitation = state.invitation;
  const emailMismatch = currentEmail && invitation && currentEmail.toLowerCase() !== invitation.email.toLowerCase();
  const unavailable = invitation && invitation.status !== "pending" && !state.accepted;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader actor={authState === "authenticated" ? actor : null} />
      <main className="mx-auto grid min-h-[calc(100vh-80px)] w-full max-w-[1180px] place-items-center px-5 py-16 sm:px-8">
        <section className="grid w-full max-w-[760px] gap-8 rounded-xl bg-card p-8 shadow-soft sm:grid-cols-[132px_minmax(0,1fr)] sm:p-12">
          <div className="grid size-[112px] place-content-center rounded-2xl border border-primary-100 bg-primary-50 text-center"><span className="text-[10px] font-bold uppercase tracking-[.14em] text-primary-600">Invitation</span><strong className="mt-2 text-4xl text-primary">→</strong></div>
          <div className="grid min-w-0 gap-6">
            {state.loading ? <LoadingRows count={3} /> : <>
              <div className="grid gap-3"><Eyebrow>Organization access</Eyebrow><h1 className="text-[clamp(1.9rem,3.4vw,2.6rem)] font-bold leading-[1.08] tracking-[-0.025em]">{invitation ? `Join ${invitation.workspace.name}` : "Invitation unavailable"}</h1>{invitation && <p className="max-w-[58ch] text-sm leading-7 text-muted-foreground"><strong className="text-neutral-600">{invitation.invited_by.name}</strong> invited <strong className="text-neutral-600">{invitation.email}</strong> to this organization as <Badge>{invitation.role}</Badge>.</p>}</div>
              {state.error && <Notice>{state.error}</Notice>}
              {state.accepted && <Notice tone="info">Invitation accepted. Sign in again when you want to choose {invitation.workspace.name} as your active organization.</Notice>}
              {unavailable && <Notice tone="info">This invitation is {invitation.status} and cannot be used again.</Notice>}
              {emailMismatch && <Notice>Sign in as {invitation.email} to accept this invitation. You are currently signed in as {currentEmail}.</Notice>}

              {invitation?.status === "pending" && !state.accepted && <div className="flex flex-wrap gap-3">
                {authState === "anonymous" ? <><ActionLink href={authLinks.signup} variant="primary">Create account and accept</ActionLink><ActionLink href={authLinks.login}>Sign in to accept</ActionLink></> : <Button variant="primary" disabled={busy || emailMismatch} onClick={accept}>{busy ? "Accepting…" : "Accept invitation"}</Button>}
                {authState !== "anonymous" && emailMismatch && <Button onClick={onLogout}>Sign out</Button>}
              </div>}
              <p className="text-xs leading-5 text-muted-foreground">Invitation links expire after seven days and can only be accepted by the exact invited email address.</p>
            </>}
          </div>
        </section>
      </main>
    </div>
  );
}
